import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Pressable, StyleSheet, Text, View } from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';

import { formatCurrency } from '../../../../components/charts/d3/format';
import { WebScreenBody } from '../../../../components/layout/WebScreenBody';
import { WebWidgetCell, WebWidgetGrid } from '../../../../components/layout/WebWidgetGrid';
import {
  EditTransactionModal,
  type TransactionEditDraft,
  type TransactionEditTarget,
} from '../../../../components/spend/EditTransactionModal';
import { PageHeader } from '../../../../components/layout/PageHeader';
import { ErrorRetry } from '../../../../components/ui/ErrorRetry';
import { GlassButton } from '../../../../components/ui/GlassButton';
import { GlassCard } from '../../../../components/ui/GlassCard';
import { RefreshButton } from '../../../../components/ui/RefreshButton';
import { useAuth } from '../../../../contexts/AuthContext';
import { useHouseholdMembership } from '../../../../contexts/HouseholdMembershipContext';
import type { SpendStackParamList } from '../../../../core/navigation/types';
import { colors, spacing, typography } from '../../../../core/theme/tokens';
import { useSpendBreakdown } from '../../../../hooks/useSpendBreakdown';
import {
  listTransactions,
  patchTransaction,
  type ApiTransaction,
} from '../../../../services/transactionService';
import type { SpendTransaction } from '../../../../types/financial';
import {
  isOwnTransaction,
  resolveTransactionOwnerLabel,
} from '../../../../utils/transactionOwnership';

type Props = NativeStackScreenProps<SpendStackParamList, 'TransactionList'>;

type BrowseTx = SpendTransaction & {
  categoryName?: string;
  direction?: 'debit' | 'credit';
  userId?: string;
  hiddenFromHousehold?: boolean;
  ownerLabel?: string | null;
  canEdit: boolean;
};

function formatDate(iso: string): string {
  const date = new Date(iso.includes('T') ? iso : `${iso}T12:00:00`);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

function formatDayHeader(iso: string): string {
  const date = new Date(iso.includes('T') ? iso : `${iso}T12:00:00`);
  if (Number.isNaN(date.getTime())) return iso.slice(0, 10);
  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatRowHint(tx: BrowseTx, showAll: boolean): string {
  const date = formatDate(tx.date);
  const parts: string[] = [];
  if (tx.ownerLabel) parts.push(tx.ownerLabel);
  if (showAll && tx.categoryName) parts.push(tx.categoryName);
  parts.push(date);
  return parts.join(' · ');
}

function groupByDate(txs: BrowseTx[]): Array<{ day: string; items: BrowseTx[] }> {
  const sorted = [...txs].sort((a, b) =>
    a.date < b.date ? 1 : a.date > b.date ? -1 : b.amount - a.amount,
  );
  const sections: Array<{ day: string; items: BrowseTx[] }> = [];
  const byDay = new Map<string, BrowseTx[]>();
  for (const tx of sorted) {
    const day = tx.date.slice(0, 10);
    const list = byDay.get(day);
    if (list) list.push(tx);
    else byDay.set(day, [tx]);
  }
  for (const [day, items] of byDay) {
    sections.push({ day, items });
  }
  return sections;
}

export function TransactionListScreen({ navigation, route }: Props) {
  const { tokens, isAuthenticated, user } = useAuth();
  const {
    view: householdView,
    household,
    memberCount,
  } = useHouseholdMembership();
  const {
    isLive,
    analyticsPeriod,
    periodKey,
    totalSpend,
    categories,
    getCategory,
    getMerchant,
  } = useSpendBreakdown({
    period: route.params.period ?? 'month',
  });

  const showAll = route.params.mode === 'all' || !route.params.categoryId;
  const { categoryId, merchantId, spendAccountId, spendAccountLabel } = route.params;

  const sampleMerchant =
    !isLive && !showAll && categoryId && merchantId
      ? getMerchant(categoryId, merchantId)
      : undefined;

  const sampleCategory =
    !isLive && !showAll && categoryId && !merchantId
      ? getCategory(categoryId)
      : undefined;

  const liveCategory =
    isLive && !showAll && categoryId ? getCategory(categoryId) : undefined;

  const [liveTxs, setLiveTxs] = useState<BrowseTx[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<TransactionEditTarget | null>(null);
  const [saving, setSaving] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const effectivePeriod = route.params.analyticsPeriod ?? analyticsPeriod;
  const effectivePeriodKey = route.params.periodKey ?? periodKey;
  const pageSize = 40;

  const mapApiTx = useCallback(
    (tx: ApiTransaction): BrowseTx => {
      const dateRaw = tx.date as unknown;
      const dateStr =
        typeof dateRaw === 'string'
          ? dateRaw
          : dateRaw instanceof Date
            ? dateRaw.toISOString()
            : String(dateRaw);
      const canEdit = isOwnTransaction(tx.userId, user?.id);
      return {
        id: tx.id,
        date: dateStr.slice(0, 10),
        merchant: tx.merchant,
        amount: Math.abs(tx.amount),
        note: tx.description || tx.merchant,
        categoryName: tx.categoryName,
        direction: tx.direction,
        userId: tx.userId,
        hiddenFromHousehold: tx.hiddenFromHousehold === true,
        ownerLabel: resolveTransactionOwnerLabel({
          userId: tx.userId,
          view: householdView,
          members: household?.members ?? [],
          memberCount,
          viewerUserId: user?.id,
        }),
        canEdit,
      };
    },
    [household?.members, householdView, memberCount, user?.id],
  );

  useEffect(() => {
    if (!isLive || !tokens?.accessToken) return;

    let cancelled = false;
    setLoading(true);
    setError(null);
    setLiveTxs([]);
    setNextCursor(null);
    setHasMore(false);

    void listTransactions({
      accessToken: tokens.accessToken,
      tab: 'spend',
      period: effectivePeriod,
      periodKey: effectivePeriodKey,
      categorySlug: showAll ? undefined : categoryId,
      merchantNormalized: showAll ? undefined : merchantId,
      spendAccountId,
      view: householdView,
      limit: pageSize,
    })
      .then((response) => {
        if (cancelled) return;
        setLiveTxs(response.transactions.map(mapApiTx));
        setNextCursor(response.nextCursor ?? null);
        setHasMore(Boolean(response.hasMore && response.nextCursor));
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    isLive,
    tokens?.accessToken,
    effectivePeriod,
    effectivePeriodKey,
    categoryId,
    merchantId,
    spendAccountId,
    showAll,
    householdView,
    mapApiTx,
    reloadKey,
  ]);

  const loadMore = () => {
    if (!isLive || !tokens?.accessToken || !nextCursor || loadingMore) return;
    setLoadingMore(true);
    void listTransactions({
      accessToken: tokens.accessToken,
      tab: 'spend',
      period: effectivePeriod,
      periodKey: effectivePeriodKey,
      categorySlug: showAll ? undefined : categoryId,
      merchantNormalized: showAll ? undefined : merchantId,
      spendAccountId,
      view: householdView,
      limit: pageSize,
      cursor: nextCursor,
    })
      .then((response) => {
        setLiveTxs((prev) => [...prev, ...response.transactions.map(mapApiTx)]);
        setNextCursor(response.nextCursor ?? null);
        setHasMore(Boolean(response.hasMore && response.nextCursor));
      })
      .catch((err: Error) => {
        setError(err.message);
      })
      .finally(() => {
        setLoadingMore(false);
      });
  };

  const handleSaveEdit = (draft: TransactionEditDraft) => {
    if (!editTarget) return;
    setSaving(true);
    void patchTransaction(editTarget.id, {
      categorySlug: draft.categorySlug,
      categoryName: draft.categoryName,
      hiddenFromHousehold: draft.hiddenFromHousehold,
    })
      .then(() => {
        setEditTarget(null);
        setReloadKey((k) => k + 1);
      })
      .catch((err: Error) => {
        setError(err.message);
      })
      .finally(() => setSaving(false));
  };

  const sampleAllTxs = useMemo((): BrowseTx[] => {
    if (isLive || !showAll) return [];
    const rows: BrowseTx[] = [];
    for (const cat of categories) {
      for (const merchant of cat.merchants) {
        for (const tx of merchant.transactions) {
          rows.push({
            ...tx,
            categoryName: cat.name,
            canEdit: false,
          });
        }
      }
    }
    return rows;
  }, [categories, isLive, showAll]);

  const sampleCategoryTxs = useMemo((): BrowseTx[] => {
    if (!sampleCategory) return [];
    const rows: BrowseTx[] = [];
    for (const merchant of sampleCategory.merchants) {
      for (const tx of merchant.transactions) {
        rows.push({
          ...tx,
          categoryName: sampleCategory.name,
          canEdit: false,
        });
      }
    }
    return rows.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
  }, [sampleCategory]);

  if (!isLive && !showAll && !sampleMerchant && !sampleCategory) {
    return (
      <WebScreenBody>
        <GlassButton variant="ghost" size="sm" onPress={() => navigation.goBack()}>
          Back
        </GlassButton>
        <GlassCard style={styles.emptyCard}>
          <Text style={styles.emptyTitle}>Category not found</Text>
          <Text style={styles.emptyBody}>This sample category is no longer available.</Text>
        </GlassCard>
      </WebScreenBody>
    );
  }

  const title = showAll
    ? spendAccountLabel || 'All transactions'
    : isLive
      ? (route.params.categoryName ?? liveCategory?.name ?? categoryId ?? 'Category')
      : sampleMerchant
        ? sampleMerchant.merchant.name
        : (route.params.categoryName ?? sampleCategory!.name);

  const eyebrow = showAll
    ? spendAccountLabel
      ? 'Account'
      : 'Spend'
    : isLive || sampleCategory
      ? 'Category'
      : sampleMerchant!.category.name;

  const subtitle = showAll
    ? `${formatCurrency(totalSpend)} · ${effectivePeriodKey} · newest first`
    : isLive
      ? `${formatCurrency(route.params.categoryAmount ?? liveCategory?.amount ?? 0)} · ${effectivePeriodKey}`
      : sampleCategory
        ? `${formatCurrency(route.params.categoryAmount ?? sampleCategory.amount)} · ${effectivePeriodKey}`
        : `${formatCurrency(sampleMerchant!.merchant.amount)} this month`;

  const transactions: BrowseTx[] = isLive
    ? liveTxs
    : showAll
      ? sampleAllTxs
      : sampleCategory
        ? sampleCategoryTxs
        : [...sampleMerchant!.merchant.transactions]
            .map((tx) => ({ ...tx, canEdit: false as const }))
            .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));

  const sections = groupByDate(transactions);

  return (
    <WebScreenBody>
      <GlassButton variant="ghost" size="sm" onPress={() => navigation.goBack()}>
        Back
      </GlassButton>

      <PageHeader
        title={title}
        subtitle={subtitle}
        onRefresh={() => setReloadKey((k) => k + 1)}
        refreshing={loading}
      >
        <Text style={styles.eyebrow}>{eyebrow}</Text>
      </PageHeader>

      {error ? (
        <ErrorRetry
          message={error}
          onRetry={() => setReloadKey((k) => k + 1)}
          refreshing={loading}
        />
      ) : null}

      <WebWidgetGrid>
        <WebWidgetCell span="full">
          <GlassCard>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>
                {showAll ? `${transactions.length} transactions` : 'Transactions'}
              </Text>
              <RefreshButton
                onPress={() => setReloadKey((k) => k + 1)}
                refreshing={loading}
                compact
              />
            </View>
            {loading ? (
              <ActivityIndicator color={colors.accent} />
            ) : transactions.length === 0 ? (
              <Text style={styles.emptyBody}>
                {isAuthenticated
                  ? showAll
                    ? 'No transactions in this period.'
                    : 'No transactions in this period for this category.'
                  : 'No transactions.'}
              </Text>
            ) : (
              <View style={styles.list}>
                {sections.map((section) => (
                  <View key={section.day} style={styles.section}>
                    <Text style={styles.dayLabel}>{formatDayHeader(section.day)}</Text>
                    {section.items.map((tx) => {
                      const signed =
                        tx.direction === 'credit'
                          ? formatCurrency(tx.amount)
                          : `-${formatCurrency(tx.amount)}`;
                      const rowInner = (
                        <>
                          <View style={styles.meta}>
                            <Text style={styles.rowLabel} numberOfLines={2}>
                              {tx.merchant || tx.note}
                            </Text>
                            <Text style={styles.rowHint} numberOfLines={1}>
                              {formatRowHint(tx, showAll)}
                            </Text>
                          </View>
                          <Text
                            style={[
                              styles.rowValue,
                              tx.direction === 'credit' && styles.rowValueCredit,
                            ]}
                          >
                            {tx.direction ? signed : formatCurrency(tx.amount)}
                          </Text>
                        </>
                      );
                      if (tx.canEdit) {
                        return (
                          <Pressable
                            key={tx.id}
                            style={styles.row}
                            onPress={() =>
                              setEditTarget({
                                id: tx.id,
                                merchant: tx.merchant,
                                categoryName: tx.categoryName,
                                hiddenFromHousehold: tx.hiddenFromHousehold,
                              })
                            }
                            accessibilityLabel={`Edit ${tx.merchant || tx.note}`}
                          >
                            {rowInner}
                          </Pressable>
                        );
                      }
                      return (
                        <View key={tx.id} style={styles.row}>
                          {rowInner}
                        </View>
                      );
                    })}
                  </View>
                ))}
                {isLive && hasMore ? (
                  <GlassButton
                    variant="secondary"
                    size="sm"
                    onPress={loadMore}
                    disabled={loadingMore}
                    style={styles.loadMore}
                  >
                    {loadingMore ? 'Loading…' : 'Load more'}
                  </GlassButton>
                ) : null}
              </View>
            )}
          </GlassCard>
        </WebWidgetCell>
      </WebWidgetGrid>

      <EditTransactionModal
        visible={editTarget != null}
        initial={editTarget}
        saving={saving}
        onClose={() => setEditTarget(null)}
        onSave={handleSaveEdit}
      />
    </WebScreenBody>
  );
}

const styles = StyleSheet.create({
  pageHeader: {
    marginBottom: spacing.sm,
    marginTop: spacing.sm,
    gap: 2,
  },
  eyebrow: {
    ...typography.caption,
    color: colors.textMuted,
    textTransform: 'uppercase',
  },
  pageTitle: {
    ...typography.title,
    color: colors.text,
    fontSize: 26,
  },
  pageSub: {
    ...typography.body,
    color: colors.textMuted,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    marginBottom: spacing.xs,
  },
  sectionTitle: {
    ...typography.headline,
    color: colors.text,
    marginBottom: spacing.md,
  },
  list: {
    gap: spacing.sm,
  },
  section: {
    gap: 2,
    marginBottom: spacing.sm,
  },
  dayLabel: {
    ...typography.label,
    color: colors.textMuted,
    textTransform: 'uppercase',
    fontSize: 10,
    marginBottom: 4,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
    minHeight: 44,
    paddingVertical: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.glassBorder,
  },
  meta: {
    flex: 1,
    gap: 2,
    minWidth: 0,
  },
  rowLabel: {
    ...typography.body,
    color: colors.text,
  },
  rowHint: {
    ...typography.caption,
    color: colors.textMuted,
  },
  rowValue: {
    ...typography.body,
    color: colors.text,
    fontWeight: '600',
    fontVariant: ['tabular-nums'],
  },
  rowValueCredit: {
    color: colors.accent,
  },
  loadMore: {
    marginTop: spacing.md,
    alignSelf: 'center',
  },
  emptyCard: {
    marginTop: spacing.md,
  },
  emptyTitle: {
    ...typography.headline,
    color: colors.text,
  },
  emptyBody: {
    ...typography.body,
    color: colors.textMuted,
    marginTop: spacing.xs,
  },
  errorCard: {
    marginBottom: spacing.md,
    borderColor: colors.warning,
  },
  errorText: {
    ...typography.body,
    color: colors.warning,
  },
});
